/**
 * `<SidebarFavoritesSection>` — the user's pinned wiki nodes, rendered as
 * a second group at the bottom of the primary app sidebar (below the 7
 * top-level nav items, above the Settings cluster).
 *
 * Originally lived inside the wiki secondary rail (`<WikiFavoritesSection>`,
 * now deleted). The 2026-05-28 move lifts it into the sidebar so favorites
 * are always one click away regardless of which feature surface the user
 * is on. The pinning interaction (right-click in the wiki tree → "Add to
 * Favorites") is unchanged — only the render location moved.
 *
 * Visual chrome mirrors `<NavItem>` (h-9, 18px leading icon, `gap-3`,
 * `bg-bg-muted` hover/active) so favorites read as rail citizens rather
 * than a transplanted wiki rail. A small caps "FAVORITES" label below a
 * `border-t border-border` divider signals the group break.
 *
 * Activation is **distance-only** (`{ distance: 5 }`) — matches the rest
 * of the codebase (board + wiki tree). The old delay+tolerance combo
 * cancelled every real grab and was the source of the wiki tree's
 * laggy-drag bug; reusing the safe pattern here.
 *
 * Logical Tailwind utilities only (`ms-`, `me-`, `ps-`, `pe-`, etc.) per
 * the `[FE/i18n]` RTL rule.
 */

import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Link, useParams } from '@tanstack/react-router'
import { ChevronRight, FileText, Folder, X as XIcon } from 'lucide-react'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WikiNode } from '@/api/types-bridge'

import { useRemoveFavorite } from '../hooks/use-remove-favorite'
import { useReorderFavorites } from '../hooks/use-reorder-favorites'
import { useWikiTree } from '../hooks/use-wiki-tree'
import { buildDocPath } from '../lib/slug'

const UNTITLED_BE_LITERAL = 'Untitled'
/** Persists the section's collapsed state across reloads (Notion / Linear /
 *  Slack convention). Independent from the primary sidebar's collapse state
 *  — the section can be collapsed inside an expanded rail or auto-bypassed
 *  when the rail itself is icon-only. */
const SECTION_COLLAPSED_KEY = 'sidebar-favorites-section-collapsed'

function readSectionCollapsedInitial(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(SECTION_COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

export interface SidebarFavoritesSectionProps {
  /**
   * Primary sidebar collapsed state. When true, rows render as 36×36
   * icon-only squares (mirroring `<NavItem>`'s collapsed layout) and the
   * caps label is hidden — the divider becomes a short centered rule.
   */
  collapsed: boolean
}

export function SidebarFavoritesSection({ collapsed }: SidebarFavoritesSectionProps) {
  const { t } = useTranslation('common')
  const { favorites } = useWikiTree()
  const reorder = useReorderFavorites()

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  // Section collapse state — persisted to localStorage, independent of the
  // primary sidebar's `collapsed` prop. When the rail itself is collapsed
  // to icons, this state is ignored (icons render as before).
  const [sectionCollapsed, setSectionCollapsed] = useState<boolean>(readSectionCollapsedInitial)
  const initialMountRef = useRef(true)
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false
      return
    }
    try {
      window.localStorage.setItem(SECTION_COLLAPSED_KEY, sectionCollapsed ? '1' : '0')
    } catch {
      // Storage quota / private mode — silent fail; state resets on reload.
    }
  }, [sectionCollapsed])

  // Defense-in-depth: only docs can be pinned at the BE layer (folders
  // raise `wiki_favorites_kind_not_allowed`). Filter pre-existing legacy
  // folder rows out of the render without requiring a backfill.
  const visibleFavorites = useMemo(() => favorites.filter((n) => n.kind !== 'folder'), [favorites])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIdx = visibleFavorites.findIndex((f) => f.id === active.id)
    const newIdx = visibleFavorites.findIndex((f) => f.id === over.id)
    if (oldIdx === -1 || newIdx === -1) return
    const reordered = arrayMove([...visibleFavorites], oldIdx, newIdx)
    reorder.mutate({ ids: reordered.map((f) => f.id) })
  }

  const isEmpty = visibleFavorites.length === 0

  // Hide the section entirely when the rail is collapsed AND empty — an
  // empty caps label + helper sentence on a 64px-wide rail is just noise.
  if (collapsed && isEmpty) return null

  // Section collapse only applies when the primary sidebar is expanded;
  // the icon-only rail bypasses the disclosure (no header to click on).
  const showBody = collapsed || !sectionCollapsed

  return (
    <section aria-labelledby="sidebar-favorites-heading" className="flex flex-col">
      {collapsed ? (
        // Collapsed rail: thin centered rule, no label / no disclosure.
        <div aria-hidden="true" className="mx-3 mt-2 border-t border-border" />
      ) : (
        <div className="mt-1 px-2.5 pt-3">
          <div aria-hidden="true" className="border-t border-border" />
          {/* Disclosure button — the entire heading row is the click target
              for toggling the section. `aria-expanded` carries the state
              for screen readers; the chevron rotates 90° when open, matching
              the wiki tree folder chevron's idiom. */}
          <button
            type="button"
            onClick={() => setSectionCollapsed((s) => !s)}
            aria-expanded={!sectionCollapsed}
            aria-controls="sidebar-favorites-body"
            className="mt-2 flex w-full items-center gap-1 rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <ChevronRight
              aria-hidden="true"
              className={`h-3 w-3 shrink-0 transition-transform ${
                sectionCollapsed ? '' : 'rotate-90'
              }`}
            />
            <span id="sidebar-favorites-heading">{t('sidebar.favoritesLabel')}</span>
          </button>
        </div>
      )}

      {!showBody ? null : isEmpty ? (
        // Empty state — no chrome, no icon, no centering. Indented to the
        // SAME content-start as a populated favorite row's label
        // (`px-2.5` outer + 18px icon + `gap-3` = ~40px → `ps-10`), NOT to
        // the FAVORITES caps header's leading edge. The deeper indent
        // telegraphs "this is the section's row slot, just empty" — the
        // eye reads the headline as sitting where the first favorite's
        // name will sit once pinned. Shallower indent reads as floating
        // text below the header (the v3 complaint).
        <div id="sidebar-favorites-body" className="mt-1 pe-3 ps-10 pt-0.5 pb-1">
          <p className="text-[12px] font-medium leading-snug text-fg-muted">
            {t('sidebar.favoritesEmptyHeadline')}
          </p>
          <p className="mt-1 text-[11px] leading-snug text-fg-subtle">
            {t('sidebar.favoritesEmptyHint')}
          </p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={visibleFavorites.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul
              id="sidebar-favorites-body"
              className={
                collapsed
                  ? 'mt-2 flex max-h-[180px] list-none flex-col gap-1 overflow-y-auto overscroll-contain p-0'
                  : // `px-2.5 py-1` matches the primary nav's padding so the
                    // favorite rows' active-state rounded fill aligns with the
                    // 7 nav items above (the prior `px-1.5 py-0` made the row
                    // bg sit narrower and feel cramped against neighbours).
                    'mt-1 flex max-h-[288px] list-none flex-col gap-0.5 overflow-y-auto overscroll-contain px-2.5 py-1'
              }
            >
              {visibleFavorites.map((node) => (
                <FavoriteRow key={node.id} node={node} collapsed={collapsed} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </section>
  )
}

/* ------------------------------- row -------------------------------- */

interface FavoriteRowProps {
  node: WikiNode
  collapsed: boolean
}

function FavoriteRow({ node, collapsed }: FavoriteRowProps) {
  const { t } = useTranslation('common')
  const removeFavorite = useRemoveFavorite()
  const params = useParams({ strict: false }) as { docId?: string }
  const isActive = params.docId === node.id

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
  })

  const isUntitled = node.name === UNTITLED_BE_LITERAL || node.name.length === 0
  const displayName = isUntitled ? t('sidebar.favoritesUntitled') : node.name
  const hasDraftBadge = node.hasDraftAhead === true || node.isPublished === false

  const handleRemoveClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    removeFavorite.mutate({ nodeId: node.id })
  }

  const rowStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  // Layout + tone strings mirror `<NavItem>` exactly so the favorites row
  // reads as a peer of the 7 nav items above. `cursor-grab` surfaces the
  // drag affordance without a separate handle; the 5px PointerSensor
  // activation distance keeps plain clicks routed to the `<Link>`.
  const layout = collapsed
    ? 'h-9 w-9 mx-auto justify-center'
    : 'h-9 w-full justify-start gap-3 px-2.5'
  const base = `group relative flex items-center rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${layout}`
  const tone = isActive
    ? 'bg-bg-muted text-fg font-semibold'
    : 'font-medium text-fg-muted hover:bg-bg-muted hover:text-fg'
  const className = `${base} ${tone} cursor-grab`

  // Emoji-first identity. Notion convention: when a page has an emoji,
  // the emoji IS the icon. Fall back to a kind glyph otherwise. (Folder
  // rows are filtered upstream today but the kind branch stays here for
  // forward compatibility if folders become favoritable later.)
  const glyph = node.emoji ? (
    <span
      aria-hidden="true"
      className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[15px] leading-none"
    >
      {node.emoji}
    </span>
  ) : node.kind === 'folder' ? (
    <Folder size={18} strokeWidth={1.75} className="shrink-0 text-fg-subtle" aria-hidden="true" />
  ) : (
    <FileText size={18} strokeWidth={1.75} className="shrink-0 text-fg-subtle" aria-hidden="true" />
  )

  // Draft indicator. Expanded rail: a "DRAFT" pill at the row's trailing
  // edge (same amber chip the wiki tree row uses, so the affordance reads
  // consistently across surfaces). Hidden on row hover so the X button can
  // claim the same end-slot. Collapsed rail: a small amber dot overlays
  // the icon — matches `<NavItem>`'s unread-dot pattern.
  const draftPill =
    !collapsed && hasDraftBadge ? (
      <span
        aria-label={t('sidebar.favoritesDraftBadge')}
        className="ms-auto inline-flex h-5 shrink-0 items-center rounded-full bg-amber-100 px-1.5 text-[10px] font-medium uppercase tracking-wide text-amber-900 group-hover:hidden dark:bg-amber-400/20 dark:text-amber-100"
      >
        {t('sidebar.favoritesDraftBadge')}
      </span>
    ) : null
  const draftDot =
    collapsed && hasDraftBadge ? (
      <span
        aria-hidden="true"
        className="absolute -end-1 -top-1 inline-flex h-2 w-2 rounded-full bg-amber-500 ring-2 ring-bg"
      />
    ) : null

  // Phase 5 Wave B — docs route to the Notion-style pretty URL; folders
  // (filtered upstream today, but kept for forward compat) keep the
  // legacy `/wiki/d/:id` form since `FolderDetailPage` reads its params
  // via TanStack.
  const linkProps =
    node.kind === 'folder'
      ? ({ to: '/wiki/d/$docId', params: { docId: node.id } } as const)
      : ({
          to: '/wiki/$slugWithTail',
          params: { slugWithTail: buildDocPath(node).replace(/^\/wiki\//, '') },
        } as const)

  return (
    <li ref={setNodeRef} style={rowStyle} className="list-none">
      <Link
        {...linkProps}
        className={className}
        aria-current={isActive ? 'page' : undefined}
        title={displayName}
        {...attributes}
        {...listeners}
      >
        <span className="relative flex shrink-0 items-center">
          {glyph}
          {draftDot}
        </span>
        {!collapsed && (
          <span
            className={['min-w-0 flex-1 truncate', isUntitled ? 'italic text-fg-subtle' : '']
              .filter(Boolean)
              .join(' ')}
          >
            {displayName}
          </span>
        )}
        {draftPill}
        {!collapsed && (
          <button
            type="button"
            onClick={handleRemoveClick}
            aria-label={t('sidebar.favoritesRemoveAria')}
            title={t('sidebar.favoritesRemoveAria')}
            className="ms-auto hidden h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-elevated hover:text-fg group-hover:inline-flex"
          >
            <XIcon className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
        {collapsed && (
          <span
            className="pointer-events-none absolute left-full top-1/2 z-50 ms-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            role="tooltip"
          >
            {displayName}
          </span>
        )}
      </Link>
    </li>
  )
}
